import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';

import Image from '@atlaskit/image';
import {
	PopoverContent,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';

import FPSChangeboardingSpotlight from '../../../common/constants/assets/fps-changeboarding-spotlight.svg';
import { type FireAnalyticsEvent } from '../../../common/types';
import { useAnalytics } from '../../../controllers/analytics';
import { useAvailableProducts } from '../../../controllers/store/bootstrap';
import { useSpotlightState } from '../../../controllers/store/spotlight';

import messages from './messages';

const spotlightAnalyticsPayload = (action: string, attributes: FireAnalyticsEvent['attributes']) =>
	({
		actionSubject: 'rovoChangeboardingSpotlight',
		attributes: {
			additionalAttributes: attributes,
		},
		eventType: 'ui',
		action,
	}) satisfies FireAnalyticsEvent;

/**
 * New spotlight implementation using @atlaskit/spotlight compositional API.
 * Renders inside a PopoverProvider via CustomSpotlightTarget.
 */
export const SearchPageOnboardingSpotlightNew = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [spotlight, { dismiss }] = useSpotlightState();
	const { fireAnalyticsEvent } = useAnalytics();
	const availableProducts = useAvailableProducts();

	const renderSpotlight = spotlight.state === 'visible' && availableProducts.length > 0;

	useEffect(() => {
		if (renderSpotlight) {
			fireAnalyticsEvent(
				spotlightAnalyticsPayload('view spotlight', {
					step: spotlight.activeSpotlight + 1,
				}),
			);
		}
	}, [fireAnalyticsEvent, renderSpotlight, spotlight]);

	if (!renderSpotlight) {
		return null;
	}

	const handleDismiss = () => {
		dismiss();

		fireAnalyticsEvent(
			spotlightAnalyticsPayload('click ok', {
				step: spotlight.activeSpotlight + 1,
			}),
		);
	};

	return (
		<PopoverContent
			isVisible={renderSpotlight}
			placement="left-end"
			shouldDismissOnClickOutside={false}
			dismiss={handleDismiss}
			offset={[0, 4]}
		>
			<SpotlightCard>
				<SpotlightHeader>
					<SpotlightHeadline>{formatMessage(messages.onboardingStep1Heading)}</SpotlightHeadline>
					<SpotlightControls>
						<SpotlightDismissControl onClick={handleDismiss} />
					</SpotlightControls>
				</SpotlightHeader>
				<SpotlightMedia>
					<Image src={FPSChangeboardingSpotlight} alt="" />
				</SpotlightMedia>
				<SpotlightBody>{formatMessage(messages.onboardingStep1Description)}</SpotlightBody>
				<SpotlightFooter>
					<SpotlightActions>
						<SpotlightPrimaryAction onClick={handleDismiss}>
							{formatMessage(messages.gotIt)}
						</SpotlightPrimaryAction>
					</SpotlightActions>
				</SpotlightFooter>
			</SpotlightCard>
		</PopoverContent>
	);
};

